import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import FormInput from '../../../components/ui/FormInput';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { showErrorToast, showSuccessToast, showLoadingToast, dismissToast } from '../../../utils/toast';
import { validators } from '../../../utils/formValidators';
import { checkDuplicateBill } from '../../../utils/validation';
import { withRetryAndOfflineQueue } from '../../../utils/retryWithBackoff';
import { announceToScreenReader } from '../../../utils/ariaUtils';
import AriaLiveRegion from '../../../components/ui/AriaLiveRegion';

const BillForm = ({ bill, onSubmit, onClose, existingBills = [] }) => {
  const [values, setValues] = useState({
    billName: '',
    amount: '',
    frequency: 'monthly',
    dueDate: '',
    category: 'utilities',
    autopayEnabled: false,
    reminderDays: 3,
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [announcement, setAnnouncement] = React.useState('');

  useEffect(() => {
    if (bill) {
      setValues({
        billName: bill?.bill_name || '',
        amount: bill?.amount || '',
        frequency: bill?.frequency || 'monthly',
        dueDate: bill?.due_date || '',
        category: bill?.category || 'utilities',
        autopayEnabled: bill?.autopay_enabled || false,
        reminderDays: bill?.reminder_days || 3,
        notes: bill?.notes || ''
      });
    }
  }, [bill]);

  const validateField = (field, value) => {
    let error = null;
    
    if (field === 'billName') {
      error = validators?.required(value, 'Bill name') || validators?.minLength(value, 3, 'Bill name');
    } else if (field === 'amount') {
      error = validators?.amount(value, 'Amount');
    } else if (field === 'frequency') {
      error = validators?.required(value, 'Frequency');
    } else if (field === 'dueDate') {
      // Due dates should be future dates
      error = validators?.date(value, 'Due date');
    } else if (field === 'category') {
      error = validators?.required(value, 'Category');
    }
    
    return error;
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    let error = validateField(field, values?.[field]);
    setErrors({ ...errors, [field]: error });
  };

  const handleChange = (field, value) => {
    setValues({ ...values, [field]: value });
    if (touched?.[field]) {
      let error = validateField(field, value);
      setErrors({ ...errors, [field]: error });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    newErrors.billName = validateField('billName', values?.billName);
    newErrors.amount = validateField('amount', values?.amount);
    newErrors.frequency = validateField('frequency', values?.frequency);
    newErrors.dueDate = validateField('dueDate', values?.dueDate);
    newErrors.category = validateField('category', values?.category);
    
    const filteredErrors = Object.fromEntries(
      Object.entries(newErrors)?.filter(([_, v]) => v !== null)
    );
    
    setErrors(filteredErrors);
    setTouched({ billName: true, amount: true, frequency: true, dueDate: true, category: true });
    
    return Object.keys(filteredErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      const errCount = Object.keys(errors)?.length || 1;
      const msg = `Please fix ${errCount} validation error${errCount > 1 ? 's' : ''} before submitting`;
      showErrorToast(msg);
      setAnnouncement(msg);
      announceToScreenReader(msg, 'assertive');
      return;
    }

    // Check for duplicate bill name
    if (!bill && checkDuplicateBill(values?.billName, existingBills)) {
      setDuplicateWarning(true);
      setPendingSubmit(values);
      return;
    }
    
    await doSubmit(values);
  };

  const doSubmit = async (data) => {
    setIsSubmitting(true);
    const loadingToastId = showLoadingToast(
      bill ? 'Updating bill...' : 'Creating bill...'
    );
    
    try {
      await withRetryAndOfflineQueue(
        () => onSubmit(data),
        { table: 'bills', operation: bill ? 'update' : 'create', data },
        { operationName: bill ? 'Update bill' : 'Add bill', maxRetries: 5, baseDelay: 1000 }
      );
      dismissToast(loadingToastId);
      const msg = bill ? 'Bill updated successfully' : 'Bill saved successfully';
      showSuccessToast(msg);
      setAnnouncement(msg);
      announceToScreenReader(msg, 'polite');
      setDuplicateWarning(false);
      setPendingSubmit(null);
    } catch (error) {
      dismissToast(loadingToastId);
      if (!error?.isOffline) {
        const msg = `Failed to ${bill ? 'update' : 'save'} bill. Please try again.`;
        showErrorToast(msg, error);
        setAnnouncement(msg);
        announceToScreenReader(msg, 'assertive');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFieldValid = (field) => {
    return touched?.[field] && !errors?.[field] && values?.[field];
  };

  const errorCount = Object.keys(errors)?.length;
  const touchedCount = Object.keys(touched)?.length;

  const frequencyOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Bi-weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' }
  ];

  const categoryOptions = [
    { value: 'groceries', label: 'Groceries' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'entertainment', label: 'Entertainment' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'dining', label: 'Dining' },
    { value: 'shopping', label: 'Shopping' },
    { value: 'housing', label: 'Housing' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'education', label: 'Education' },
    { value: 'other', label: 'Other' }
  ];

  const reminderOptions = [
    { value: 1, label: '1 day before' },
    { value: 3, label: '3 days before' },
    { value: 7, label: '7 days before' },
    { value: 14, label: '14 days before' }
  ];

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-card rounded-xl border shadow-elevation-4 w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-slide-up">
        <div className="sticky top-0 bg-card border-b p-4 md:p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10">
              <Icon name="Receipt" size={20} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <div>
              <h2 id="bill-form-title" className="text-lg md:text-xl font-semibold text-foreground">
                {bill ? 'Edit Bill' : 'Add New Bill'}
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground">Manage your recurring bill</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-all duration-250 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Close bill form"
            disabled={isSubmitting}
          >
            <Icon name="X" size={20} color="var(--color-foreground)" strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-4 md:p-6 space-y-4"
          aria-label={bill ? 'Edit bill form' : 'Add new bill form'}
          aria-labelledby="bill-form-title"
          noValidate
        >
          <AriaLiveRegion message={announcement} priority="assertive" />

          {/* Error Count Badge */}
          {touchedCount > 0 && errorCount > 0 && (
            <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center justify-center w-7 h-7 rounded-full bg-red-500 text-white text-xs font-bold flex-shrink-0">
                {errorCount}
              </div>
              <p className="text-sm text-red-700 font-medium">
                {errorCount} field{errorCount > 1 ? 's' : ''} require{errorCount === 1 ? 's' : ''} attention before submitting
              </p>
            </div>
          )}
          {/* Duplicate Bill Warning */}
          {duplicateWarning && (
            <div className="p-4 bg-warning/10 border border-warning rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={18} color="var(--color-warning)" strokeWidth={2} className="flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-warning mb-1">Possible Duplicate Bill</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    A bill named &ldquo;<strong>{values?.billName}</strong>&rdquo; already exists. Do you want to add it anyway?
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { setDuplicateWarning(false); if (pendingSubmit) doSubmit(pendingSubmit); }}
                      className="px-3 py-1.5 text-xs font-medium bg-warning text-white rounded-lg hover:bg-warning/90 transition-colors"
                    >
                      Add Anyway
                    </button>
                    <button
                      type="button"
                      onClick={() => { setDuplicateWarning(false); setPendingSubmit(null); }}
                      className="px-3 py-1.5 text-xs font-medium bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          <FormInput
            label="Bill Name"
            type="text"
            required
            placeholder="e.g., Hydro Bill, Netflix, Internet Service"
            value={values?.billName}
            onChange={(e) => handleChange('billName', e?.target?.value)}
            onBlur={() => handleBlur('billName')}
            error={touched?.billName && errors?.billName}
            success={isFieldValid('billName')}
            maxLength={100}
            helpText="Name of the recurring bill or subscription (min 3 characters)"
            description="At least 3 characters"
            disabled={isSubmitting}
            ariaLabel="Bill name"
            name="bill-name"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Amount"
              type="number"
              required
              placeholder="e.g., 150.00"
              value={values?.amount}
              onChange={(e) => handleChange('amount', e?.target?.value)}
              onBlur={() => handleBlur('amount')}
              error={touched?.amount && errors?.amount}
              success={isFieldValid('amount')}
              min="0"
              step="0.01"
              prefix="$"
              helpText="Bill amount must be greater than $0.00"
              description="Monthly bill amount in CAD"
              disabled={isSubmitting}
              ariaLabel="Bill amount in Canadian dollars"
              name="bill-amount"
            />

            <Select
              label="Frequency"
              required
              options={frequencyOptions}
              value={values?.frequency}
              onChange={(value) => handleChange('frequency', value)}
              error={touched?.frequency && errors?.frequency}
              disabled={isSubmitting}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Due Date"
              type="date"
              required
              placeholder=""
              value={values?.dueDate}
              onChange={(e) => handleChange('dueDate', e?.target?.value)}
              onBlur={() => handleBlur('dueDate')}
              error={touched?.dueDate && errors?.dueDate}
              success={isFieldValid('dueDate')}
              helpText="Next due date for this recurring bill"
              disabled={isSubmitting}
              ariaLabel="Due date for bill"
              name="due-date"
            />

            <Select
              label="Category"
              required
              options={categoryOptions}
              value={values?.category}
              onChange={(value) => handleChange('category', value)}
              error={touched?.category && errors?.category}
              disabled={isSubmitting}
            />
          </div>

          <Select
            label="Reminder"
            required
            options={reminderOptions}
            value={values?.reminderDays}
            onChange={(value) => handleChange('reminderDays', value)}
            disabled={isSubmitting}
          />

          <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
            <input
              type="checkbox"
              id="autopay"
              name="autopay"
              checked={values?.autopayEnabled}
              onChange={(e) => handleChange('autopayEnabled', e?.target?.checked)}
              className="h-4 w-4 rounded border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              disabled={isSubmitting}
              aria-label="Enable autopay for this bill"
            />
            <label htmlFor="autopay" className="text-sm font-medium text-foreground cursor-pointer">
              Enable Autopay
            </label>
          </div>

          <div>
            <label htmlFor="bill-notes" className="text-sm font-medium text-foreground mb-2 block">Notes (Optional)</label>
            <textarea
              id="bill-notes"
              name="bill-notes"
              placeholder="Add any additional notes..."
              value={values?.notes}
              onChange={(e) => handleChange('notes', e?.target?.value)}
              rows={3}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              disabled={isSubmitting}
              aria-label="Additional notes for this bill"
            />
          </div>

          <div className="flex items-center gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName={isSubmitting ? 'Loader2' : 'Check'}
              iconPosition="left"
              fullWidth
              disabled={isSubmitting || Object.keys(errors)?.length > 0}
              disabledTooltip={Object.keys(errors)?.length > 0 ? 'Please fix all errors before submitting' : undefined}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Icon name="Loader2" size={16} color="currentColor" strokeWidth={2} className="animate-spin" />
                  {bill ? 'Updating...' : 'Saving...'}
                </span>
              ) : (
                bill ? 'Update Bill' : 'Save Bill'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BillForm;